import {
  FileTextIcon,
  ListBulletIcon,
  Pencil2Icon,
  PersonIcon,
  TrashIcon,
  UploadIcon,
} from '@radix-ui/react-icons';
import { Box, Button, Flex, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import {
  MlbLogoIcon,
  RowsAddIcon,
} from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonHitterAvatar } from 'components/common/hitters/avatar';
import { HitterRosterStatusIcon } from 'components/common/hitters/hitter-roster-status-icon';
import { HitterSideBadge } from 'components/common/hitters/hitter-side-badge';
import { CommonTableHoC } from 'components/common/table';
import { SectionHeader } from 'components/sections/header';
import { CsvUploadDialog } from 'components/sections/hitter-library/dialogs/csv-upload';
import { HitterLibraryDuplicateCallout } from 'components/sections/hitter-library/duplicate-callout';
import { HitterLibraryFooter } from 'components/sections/hitter-library/footer';
import { HitterLibraryFiltersNew } from 'components/sections/hitter-library/new/filters';
import { HitterLibraryTeamLogo } from 'components/sections/hitter-library/team-logo';
import { parseISO } from 'date-fns';
import { ACTIONS_KEY, CrudAction, TABLES } from 'enums/tables';
import useSyncHittersFilters from 'hooks/hitters/useSyncHittersFilters';
import { t } from 'i18next';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { IMenuAction } from 'interfaces/i-menus';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITableNoData } from 'interfaces/tables/no-data';
import { ITablePageable } from 'interfaces/tables/pagination';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IHitter } from 'lib_ts/interfaces/i-hitter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { paths } from 'routes/paths';
import { AdminTeamsService } from 'services/admin/teams.service';
import { useAuthStore } from 'stores/use-auth-store';
import useHittersStore from 'stores/use-hitters-store';

// shown for Analytics 2.0
export const HitterLibraryNew = () => {
  const navigate = useNavigate();

  useSyncHittersFilters();

  const current = useAuthStore((s) => s.current);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);
  const team = useAuthStore((s) => s.team);

  const loading = useHittersStore((s) => s.loading);
  const filteredHitters = useHittersStore((s) => s.filteredHitters);

  // reset filters on unmount
  useEffect(() => {
    return () => {
      useHittersStore.getState().resetFilters();
    };
  }, []);

  const noDataProps: ITableNoData = {
    noDataHeader: 'No Hitters to Display',
    noDataBody: 'No hitters match your search',
  };

  const [dialogCsv, setDialogCsv] = useState<number>();

  const columns = useMemo(() => {
    const output: ITypedTableColumn<IHitter>[] = [
      {
        key: 'name',
        label: 'common.name',
        formatFn: (hitter) => {
          return (
            <Flex gap="2" align="center" pt="4" pb="4">
              <CommonHitterAvatar size="4" hitter={hitter} />
              <Text>{StringHelper.shortenName(hitter.name)}</Text>
              <Box flexGrow="1">{/* push the badge to the right */}</Box>
              <HitterSideBadge value={hitter.side} />
            </Flex>
          );
        },
      },
      {
        key: 'level',
        label: 'common.level',
        formatFn: (hitter) => {
          return (
            <Flex gap="2" align="center">
              <HitterLibraryTeamLogo size="16px" hitter={hitter} />
              <Text>{hitter.level}</Text>
            </Flex>
          );
        },
      },
      {
        key: '_mx_usageData',
        label: 'hitters.usage-trend',
        sortRowsFn: (a, b, dir) => {
          // sort by last active
          const [aVal, bVal] = [a, b].map((h) =>
            h.lastActive ? parseISO(h.lastActive).getTime() : 0
          );

          return dir * (aVal - bVal);
        },
        formatFn: (hitter) => {
          const data = hitter._mx_usageData ?? [];
          const xMax =
            data.length === 0 ? 0 : Math.max(...data.map((d) => d.i));

          return (
            <ResponsiveContainer width="100%" height={40}>
              <AreaChart data={data}>
                <XAxis
                  domain={[0, xMax]}
                  dataKey="i"
                  type="number"
                  padding={{ left: 0, right: 0 }}
                  height={0} // prevent the axis from pushing the chart up
                  tickLine={false}
                  axisLine={false}
                  tick={false}
                />
                <YAxis
                  domain={[0, 'auto']}
                  padding={{ top: 0, bottom: 0 }}
                  width={0} // prevent the axis from pushing the chart right
                  tickLine={false}
                  axisLine={false}
                  tick={false}
                />
                <defs>
                  <linearGradient
                    id={`usage-fade-${hitter._id}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor="var(--accent-9)"
                      stopOpacity={0.75}
                    />
                    <stop
                      offset="100%"
                      stopColor="var(--accent-9)"
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <Area
                  dataKey="v"
                  type="linear"
                  isAnimationActive={false}
                  stroke="var(--accent-11)"
                  fill={`url(#usage-fade-${hitter._id})`}
                  fillOpacity={1}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          );
        },
      },
      {
        key: '_mx_totalSessions',
        label: 'hitters.sessions',
      },
      {
        key: '_mx_totalShots',
        label: 'hitters.total-shots',
      },
      {
        key: '_mx_avgShotsPerSession',
        label: 'hitters.avg-shots-per-session',
        decimals: 1,
      },
      {
        key: 'status',
        label: '',
        align: 'right',
        formatFn: (hitter) => {
          return <HitterRosterStatusIcon status={hitter.status} />;
        },
        disableSort: true,
      },
      {
        key: ACTIONS_KEY,
        label: '',
        actions: [
          {
            prefixIcon: <PersonIcon />,
            label: 'hitters.view-profile',
            onClick: (h: IHitter) =>
              navigate(paths.hitterLibrary.profile(h._id)),
          },
          {
            prefixIcon: <Pencil2Icon />,
            label: 'common.edit',
            onClick: (h: IHitter) =>
              useHittersStore.setState({
                dialogCRUD: {
                  key: Date.now(),
                  action: CrudAction.Update,
                  models: [h],
                },
              }),
          },
          {
            prefixIcon: <TrashIcon />,
            label: 'common.delete',
            color: RADIX.COLOR.DANGER,
            onClick: (h: IHitter) =>
              useHittersStore.setState({
                dialogCRUD: {
                  key: Date.now(),
                  action: CrudAction.Delete,
                  models: [h],
                },
              }),
          },
        ],
      },
    ];

    return output;
  }, []);

  const checkedActions = useCallback((checked: IHitter[]) => {
    const output: IMenuAction[] = [
      {
        prefixIcon: <TrashIcon />,
        color: RADIX.COLOR.DANGER,
        label: 'common.delete',
        onClick: () =>
          useHittersStore.setState({
            dialogCRUD: {
              key: Date.now(),
              action: CrudAction.Delete,
              models: checked,
            },
          }),
      },
    ];

    return output;
  }, []);

  const pagination: ITablePageable = {
    paginationID: PaginationID.HitterLibrary,
    total: filteredHitters.length,
    enablePagination: true,
    pageSizes: TABLES.PAGE_SIZES.MD,
  };

  return (
    <ErrorBoundary identifier="HitterLibrary">
      <Flex direction="column" gap="3" style={{ height: '100%' }}>
        <SectionHeader
          header={t('hitters.players')}
          secondaryAction={
            <Button
              variant={RADIX.BUTTON.VARIANT.SECONDARY}
              onClick={() => {
                NotifyHelper.info({
                  message_md: 'Coming soon',
                });
              }}
            >
              <UploadIcon /> {t('common.share-view')}
            </Button>
          }
          actionsMenuTrigger={
            <Button>
              <RowsAddIcon /> {t('common.add-or-update-hitters')}
            </Button>
          }
          actions={[
            {
              group: '_1',
              prefixIcon: <MlbLogoIcon />,
              label: 'MLB Stats API',
              invisible:
                // only allowed for admins and team admins
                !userRoleFeatureFlags.adminTeams.statsApiHitterImport ||
                // only allowed for teams that are allowed to use Stats API
                !current.mlb_stats_api ||
                // only allowed for teams w/ an orgID configured
                !team?.stats_api_orgId,
              description: team?.enable_hitters_import
                ? 'This feature is already enabled for your organization'
                : "Automatically update your hitter library with your organization's rosters",
              disabled:
                loading ||
                // hitter import is already enabled
                !!team?.enable_hitters_import,
              onClick: async () => {
                if (!team) {
                  return;
                }

                const updated =
                  await AdminTeamsService.getInstance().updateTeam({
                    _id: team._id,
                    enable_hitters_import: true,
                  });

                if (updated) {
                  useAuthStore.getState().setTeam(updated);

                  NotifyHelper.success({
                    message_md:
                      'Hitter sync via MLB Stats API has been enabled.',
                  });

                  // start a first import for the user's team
                  const importSuccess =
                    await AdminTeamsService.getInstance().importStatsApiHitters(
                      team._id
                    );

                  if (importSuccess) {
                    NotifyHelper.success({
                      message_md:
                        'An initial import has completed successfully. Refreshing hitters...',
                    });

                    useHittersStore.setState({ lastChanged: Date.now() });
                  }
                }
              },
            },
            {
              group: '_2',
              prefixIcon: <FileTextIcon />,
              label: 'CSV Upload',
              description: 'Upload a CSV file with multiple hitters',
              disabled: loading,
              onClick: () => setDialogCsv(Date.now()),
            },
            {
              group: '_3',
              prefixIcon: <ListBulletIcon />,
              label: 'Manual',
              description: 'Fill a form to create a hitter',
              disabled: loading,
              onClick: () =>
                useHittersStore.setState({
                  dialogCRUD: {
                    key: Date.now(),
                    action: CrudAction.Create,
                    models: [],
                  },
                }),
            },
          ]}
          extendedActionsMenu
        />

        <Flex
          direction="column"
          gap="3"
          style={{
            height: '100%',
          }}
        >
          <HitterLibraryFiltersNew />

          <HitterLibraryDuplicateCallout />

          <CommonTableHoC
            dataTestID="HitterLibraryNewTable"
            identifier="HitterLibraryNewTable"
            loading={loading}
            displayColumns={columns}
            displayData={filteredHitters}
            checkboxColumnIndex={0}
            checkedActions={checkedActions}
            defaultSort={{ key: '_mx_usageData', dir: -1 }}
            onRowClick={(hitter: IHitter) =>
              navigate(paths.hitterLibrary.profile(hitter._id))
            }
            {...pagination}
            enableSort
            vFlex
          />

          <HitterLibraryFooter hitters={filteredHitters} />
        </Flex>
      </Flex>

      {dialogCsv && (
        <CsvUploadDialog
          key={dialogCsv}
          onClose={() => setDialogCsv(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
